import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, RevealLines } from "@/components/Reveal";
import { RegionMap } from "@/components/RegionMap";
import { CtaBand } from "@/components/CtaBand";
import { ATMO, IMG, SITE } from "@/config/site";

const VALUES = [
  { n: "01", t: "Persönlich", d: "Sie sprechen direkt mit dem Inhaber – kurze Wege, klare Absprachen." },
  { n: "02", t: "Individuell", d: "Jeder Garten bekommt eine eigene Lösung statt einer Standardvorlage." },
  { n: "03", t: "Sorgfältig", d: "Fachgerechter Aufbau, saubere Baustelle, ordentliche Übergabe." },
  { n: "04", t: "Regional", d: "Verwurzelt in Bremen – wir kennen Böden, Klima und Bauweisen vor Ort." },
];

export default function UeberUns() {
  return (
    <>
      <Seo
        title="Über uns | Hanse Garten Bremen – Gartengestaltung mit Anspruch"
        description="Hanse Garten Bremen verbindet natürliche Materialien, durchdachte Gestaltung und handwerkliche Umsetzung. Inhaber Salih Koluman, Bremen."
        path="/ueber-uns"
        image={ATMO.editorial}
      />
      <PageHero
        eyebrow="Über uns"
        titleLines={["Hanse Garten", "Bremen"]}
        text="Wir verbinden natürliche Materialien, durchdachte Gestaltung und handwerkliche Umsetzung."
        image={ATMO.editorial}
        breadcrumb="Über uns"
      />

      <section className="bg-[#F3EDE2] py-24 md:py-36">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="grid gap-16 lg:grid-cols-[0.95fr_1.05fr] lg:gap-24">
            <Reveal className="overflow-hidden blob-mask">
              <img
                src={IMG.terrasse}
                alt="Strukturierter Garten mit Rasenweg und Beeteinfassungen von Hanse Garten Bremen"
                className="aspect-[4/5] w-full object-cover"
              />
            </Reveal>
            <div>
              <p className="eyebrow">Das Unternehmen</p>
              <RevealLines
                lines={["Ein Ansprechpartner", "von der Idee bis", "zur Übergabe."]}
                as="h2"
                className="display-xl mt-5 text-[9vw] leading-[0.94] text-[#17251C] md:text-[3.4vw]"
              />
              <Reveal delay={0.15}>
                <div className="mt-8 space-y-5 text-base leading-relaxed text-[#6E7A69]">
                  <p>
                    Hanse Garten Bremen ist ein Einzelunternehmen von {SITE.owner} mit Sitz in Bremen. Wir planen,
                    bauen und pflegen Gärten und Außenanlagen – für private Grundstücke ebenso wie für Flächen rund um
                    Neubauten.
                  </p>
                  <p>
                    Uns ist wichtig, dass Sie wissen, was passiert: Wir besprechen Ihr Vorhaben ehrlich, erklären
                    Möglichkeiten und Grenzen und halten den Ablauf transparent. Was wir zusagen, setzen wir um.
                  </p>
                  <p>
                    Der Fokus liegt auf Bremen, Bremen-Nord und dem direkten Umland. Kurze Anfahrtswege ermöglichen
                    persönliche Betreuung – auch nach Abschluss der Arbeiten.
                  </p>
                </div>
                <dl className="mt-12 grid gap-8 border-t border-[#17251C]/14 pt-8 sm:grid-cols-2">
                  <div>
                    <dt className="eyebrow">Inhaber</dt>
                    <dd className="display-xl mt-3 text-xl text-[#17251C]">{SITE.owner}</dd>
                  </div>
                  <div>
                    <dt className="eyebrow">Rechtsform</dt>
                    <dd className="display-xl mt-3 text-xl text-[#17251C]">{SITE.legalForm}</dd>
                  </div>
                  <div>
                    <dt className="eyebrow">Sitz</dt>
                    <dd className="display-xl mt-3 text-xl text-[#17251C]">
                      {SITE.street}, {SITE.zipCity}
                    </dd>
                  </div>
                  <div>
                    <dt className="eyebrow">Kontakt</dt>
                    <dd className="display-xl mt-3 text-xl text-[#17251C]">WhatsApp {SITE.whatsappNumber}</dd>
                    <dd className="mt-2 break-all text-base text-[#6E7A69]">{SITE.email}</dd>                  </div>
                </dl>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#2F3D2E] py-24 md:py-36">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <p className="eyebrow !text-[#DCCDB4]">Werte</p>
          <RevealLines
            lines={["Wofür wir stehen."]}
            as="h2"
            className="display-xl mt-5 text-[10vw] leading-[0.92] text-[#F3EDE2] md:text-[3.8vw]"
          />
          <div className="mt-16 grid gap-x-10 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.07}>
                <span className="text-[0.62rem] font-bold uppercase tracking-[0.3em] text-[#B4623C]">{v.n}</span>
                <h3 className="display-xl mt-4 text-2xl text-[#F3EDE2]">{v.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#B8C3B2]">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F3EDE2] py-24 md:py-36">
        <div className="mx-auto grid max-w-[1480px] gap-14 px-5 md:px-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <div>
            <p className="eyebrow">Einsatzgebiet</p>
            <RevealLines
              lines={["Bremen &", "Umgebung."]}
              as="h2"
              className="display-xl mt-5 text-[10vw] leading-[0.92] text-[#17251C] md:text-[3.6vw]"
            />
            <Reveal delay={0.15}>
              <p className="mt-7 max-w-md text-base leading-relaxed text-[#6E7A69]">
                Unser Arbeitsgebiet liegt rund um Bremen. So bleiben Wege kurz und Termine verlässlich.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <RegionMap />
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
